import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCoA } from "../../context/CoAContext";
import { useCompany } from "../../context/CompanyContext";
import Papa from "papaparse";
import "./AddAccountForm.css";

// (Optional) icons – remove if you’re not using lucide-react
// import { Plus, Upload, X, Check, Download, Hash, Type as TypeIcon, Folder } from "lucide-react";

export default function AddAccountForm() {
  // ❗ All hooks must be called unconditionally, at the top level:
  const { accounts, setAccounts } = useCoA();
  const { companyId, companyRole } = useCompany();

  const [isOpen, setIsOpen] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [parseCount, setParseCount] = useState(0);

  const [formData, setFormData] = useState({
    account_number: "",
    name: "",
    type: "Asset",
    category: "",
  });

  const modalRef = useRef(null);
  const dropRef = useRef(null);

  // Suggest next account number (derived)
  const suggestedNumber = useMemo(() => {
    const nums = (accounts || [])
      .map((a) => String(a.account_number || "").trim())
      .filter(Boolean)
      .filter((s) => /^\d+$/.test(s))
      .map((s) => Number(s));
    if (!nums.length) return "";
    const max = Math.max(...nums);
    const next = String(max + 1);
    const maxLen = Math.max(...nums.map((n) => String(n).length));
    return next.padStart(maxLen, "0");
  }, [accounts]);

  // Keyboard close (Esc)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Drag & drop CSV listeners
  useEffect(() => {
    const node = dropRef.current;
    if (!node) return;
    const prevent = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const onDrop = (e) => {
      prevent(e);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileUpload(e.dataTransfer.files[0]);
      }
    };
    const events = ["dragenter", "dragover", "dragleave", "drop"];
    events.forEach((ev) => node.addEventListener(ev, prevent));
    node.addEventListener("drop", onDrop);
    return () => {
      events.forEach((ev) => node.removeEventListener(ev, prevent));
      node.removeEventListener("drop", onDrop);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropRef]);

  // Sample CSV data URI
  const sampleCsvHref = useMemo(() => {
    const rows = [
      ["account_number", "name", "type", "category"],
      ["1000", "Cash", "Asset", "Current Asset"],
      ["2000", "Accounts Payable", "Liability", "Current Liability"],
      ["3000", "Owner's Equity", "Equity", "Equity"],
      ["4000", "Sales", "Revenue", "Operating Revenue"],
      ["5000", "Office Supplies", "Expense", "Operating Expense"],
    ];
    const csv = rows.map((r) => r.join(",")).join("\n");
    return "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
  }, []);

  // ─── Helpers (plain functions are fine anywhere) ─────────────────────────────
  const handleChange = (e) =>
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleFileUpload = (file) => setCsvFile(file || null);

  const generateNewAccountNumber = (current) => {
    const curr = String(current || "0");
    return (parseInt(curr, 10) + 1).toString().padStart(curr.length, "0");
    // keeps padding width
  };

  const insertAccount = async (account) => {
    let result = null;
    let attempts = 10;
    while (!result && attempts > 0) {
      const { data, error } = await supabase
        .from("chart_of_accounts")
        .insert([account])
        .select("*");
      if (error) {
        if (error.code === "23505") {
          account.account_number = generateNewAccountNumber(
            account.account_number
          );
          attempts--;
          continue;
        }
        throw error;
      }
      if (data && data.length) result = data[0];
    }
    if (!result) throw new Error("Failed to insert account after retries");
    return result;
  };

  const handleUploadCSV = async () => {
    if (!csvFile) return alert("Select a CSV file first.");
    if (!companyId) return alert("Company context missing.");

    setUploadErrors([]);
    setIsParsing(true);
    setParseCount(0);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async ({ data, errors }) => {
        setIsParsing(false);
        if (errors.length) setUploadErrors(errors.map((e) => e.message));
        try {
          const rows = data
            .map((r) => ({
              account_number: String(r.account_number || "").trim(),
              name: String(r.name || "").trim(),
              type: String(r.type || "").trim() || "Asset",
              category: String(r.category || "").trim() || "Uncategorized",
            }))
            .filter((r) => r.account_number && r.name);

          if (!rows.length) {
            setUploadErrors((prev) => [
              ...prev,
              "No valid rows. Required: account_number, name, type, category.",
            ]);
            return;
          }

          const inserted = [];
          for (let i = 0; i < rows.length; i++) {
            const acct = { ...rows[i], company_id: companyId };
            try {
              const rec = await insertAccount(acct);
              inserted.push(rec);
              setParseCount(i + 1);
            } catch (err) {
              setUploadErrors((prev) => [
                ...prev,
                `Row ${i + 1}: ${err.message}`,
              ]);
            }
          }
          if (inserted.length) setAccounts((prev) => [...prev, ...inserted]);
          alert(`Imported ${inserted.length} account(s).`);
          setCsvFile(null);
        } catch (err) {
          console.error(err);
          alert("Unexpected error during CSV upload.");
        }
      },
      error: (err) => {
        setIsParsing(false);
        setUploadErrors([err.message]);
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!companyId) return alert("Company context missing.");
    if (!formData.account_number || !formData.name)
      return alert("Account number and name are required.");

    try {
      const payload = { ...formData, company_id: companyId };
      const { data, error } = await supabase
        .from("chart_of_accounts")
        .insert([payload])
        .select("*");

      if (error) throw error;
      if (!data || !data.length) throw new Error("No data returned.");

      setAccounts((prev) => [...prev, data[0]]);
      alert("Account added.");
      setFormData({
        account_number: "",
        name: "",
        type: "Asset",
        category: "",
      });
      setIsOpen(false);
    } catch (err) {
      console.error(err);
      alert(`Error: ${err.message}`);
    }
  };

  // ❗ Do NOT return early before hooks — render null based on role instead:
  const isAdmin = companyRole === "admin";

  return (
    <div className="add-account-form">
      {isAdmin && (
        <button onClick={() => setIsOpen(true)} className="open-modal-btn">
          {/* <Plus size={16} /> */}
          <span>+ Add Account</span>
        </button>
      )}

      {isAdmin && isOpen && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <div className="modal-content-account" ref={modalRef}>
            <button className="modal-close" onClick={() => setIsOpen(false)}>
              {/* <X size={18} /> */}×
            </button>

            <h2>Create New Account</h2>
            <p className="subtle">Add a single account or import via CSV.</p>

            <div className="modal-grid">
              {/* Left pane: form */}
              <form onSubmit={handleSubmit} className="account-form">
                <div className="two-col">
                  <div className="input-group">
                    <label>
                      {/* <Hash size={14} /> */}
                      Account Number <span className="req">*</span>
                    </label>
                    <input
                      name="account_number"
                      placeholder={suggestedNumber || "e.g., 1000"}
                      value={formData.account_number}
                      onChange={handleChange}
                      required
                    />
                    {suggestedNumber && (
                      <small className="hint">
                        Suggested next: <strong>{suggestedNumber}</strong>
                      </small>
                    )}
                  </div>

                  <div className="input-group">
                    <label>
                      {/* <TypeIcon size={14} /> */}
                      Account Name <span className="req">*</span>
                    </label>
                    <input
                      name="name"
                      placeholder="e.g., Cash"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="two-col">
                  <div className="input-group">
                    <label>Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                    >
                      <option>Asset</option>
                      <option>Liability</option>
                      <option>Equity</option>
                      <option>Revenue</option>
                      <option>Expense</option>
                      <option>Cost of Sales</option>
                    </select>
                  </div>

                  <div className="input-group">
                    <label>
                      {/* <Folder size={14} /> */}
                      Category
                    </label>
                    <input
                      name="category"
                      placeholder="e.g., Current Asset"
                      value={formData.category}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="modal-buttons">
                  <button type="submit" className="primary">
                    {/* <Check size={16} /> */}
                    <span>Save Account</span>
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>

              {/* Right pane: CSV upload */}
              <section className="csv-upload-section2">
                <div className="csv-header">
                  <h3>Bulk Import (CSV)</h3>
                  <a
                    href={sampleCsvHref}
                    download="chart_of_accounts_sample.csv"
                    className="download-sample-btn-acct"
                  >
                    {/* <Download size={14} /> */}
                    <span>Sample CSV</span>
                  </a>
                </div>

                <div className="dropzone" ref={dropRef}>
                  {/* <Upload size={18} /> */}
                  <p>
                    Drag & drop your CSV here or{" "}
                    <label className="file-trigger">
                      browse
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                      />
                    </label>
                  </p>
                  {csvFile && <span className="file-pill">{csvFile.name}</span>}
                </div>

                <button
                  onClick={handleUploadCSV}
                  id="csv-btn"
                  disabled={!csvFile || isParsing}
                >
                  {isParsing ? "Parsing…" : "Upload CSV"}
                </button>

                {isParsing && (
                  <div className="progress">
                    <div className="bar" style={{ width: "45%" }} />
                    <small>Importing… {parseCount} row(s) processed</small>
                  </div>
                )}

                {uploadErrors.length > 0 && (
                  <ul className="error-messages">
                    {uploadErrors.map((msg, i) => (
                      <li key={i}>{msg}</li>
                    ))}
                  </ul>
                )}

                <div className="csv-hint">
                  <p>
                    Required columns: <code>account_number</code>,{" "}
                    <code>name</code>, <code>type</code>, <code>category</code>.
                    Duplicate numbers will auto-increment.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
